import {
  Phone,
  Mail,
  Calendar,
  Building2,
  FileText,
  Shield,
} from "lucide-react";
import { Card } from "src/vibe-code/rcm-v2/components/ui/card";
import { Button } from "src/vibe-code/rcm-v2/components/ui/button";
import { Badge } from "src/vibe-code/rcm-v2/components/ui/badge";
import type { Employee } from "src/vibe-code/rcm-v2/types";
import { formatEmployeeRole } from "src/vibe-code/rcm-v2/types";
import { formatDistanceToNow } from "date-fns";

interface EmployeesTableProps {
  employees: Employee[];
  onEmployeeClick: (employee: Employee) => void;
}

export function EmployeesTable({
  employees,
  onEmployeeClick,
}: EmployeesTableProps) {
  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-semibold mb-2">Employees</h2>
        <p className="text-muted-foreground">
          All employees across your dealer network
        </p>
      </div>

      <Card className="overflow-hidden shadow-custom-md">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="table-header">
              <tr>
                <th className="text-left p-4 font-semibold">Name</th>
                <th className="text-left p-4 font-semibold">Role</th>
                <th className="text-left p-4 font-semibold">Dealer Name</th>
                <th className="text-left p-4 font-semibold">Email</th>
                <th className="text-left p-4 font-semibold">Last Updated</th>
                <th className="text-left p-4 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {employees.map((employee) => (
                <tr key={employee.id} className="table-row">
                  <td className="p-4">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => onEmployeeClick(employee)}
                        className="font-medium text-primary hover:text-primary-dark transition-colors text-left"
                      >
                        {employee.first_name} {employee.last_name}
                      </button>
                      {employee.role === "GENERAL_MANAGER" && (
                        <div title="GM Permissions">
                          <Shield className="w-4 h-4 text-accent" />
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="p-4">
                    <Badge variant="outline" className="text-xs">
                      {formatEmployeeRole(employee.role)}
                    </Badge>
                  </td>
                  <td className="p-4">
                    <div className="flex items-center space-x-2">
                      <Building2 className="w-4 h-4 text-muted-foreground" />
                      <span className="text-sm">{employee.dealer.name}</span>
                    </div>
                  </td>
                  <td className="p-4">
                    <div className="flex items-center space-x-2 text-sm">
                      <Mail className="w-3 h-3 text-muted-foreground" />
                      <span className="truncate max-w-48">
                        {employee.email}
                      </span>
                    </div>
                  </td>
                  <td className="p-4">
                    <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                      <Calendar className="w-4 h-4" />
                      <span>
                        {employee.updated_at &&
                        !isNaN(new Date(employee.updated_at).getTime())
                          ? formatDistanceToNow(new Date(employee.updated_at), {
                              addSuffix: true,
                            })
                          : "Never updated"}
                      </span>
                    </div>
                  </td>
                  <td className="p-4">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onEmployeeClick(employee)}
                    >
                      View Details
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
